import type { CollectionData, ProjectDoc } from '@tomo/core';
import { toast } from '@tomo/ui';
import { ingestFile } from '../../data/blobs.ts';
import { fileKind } from '../../lib/files.ts';

const MAX_FILE_BYTES = 200 * 1024 * 1024;

const stripExt = (name: string) => name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || name;

/**
 * Store files and make one record per file, named after it. A picture becomes the record's cover;
 * anything else needs somewhere to live, so it goes in the collection's first Files field. Returns
 * the new record ids.
 */
export async function uploadAssets(project: ProjectDoc, collection: CollectionData, files: File[], extraValues: Record<string, unknown> = {}): Promise<string[]> {
  if (!files.length) return [];
  const fileField = collection.fields.find((f) => f.type === 'file');
  const tagsField = collection.fields.find((f) => f.role === 'tags');
  const tooBig = files.filter((f) => f.size > MAX_FILE_BYTES);
  let ok = files.filter((f) => f.size <= MAX_FILE_BYTES);
  if (tooBig.length) toast.warning(`${tooBig.length} file${tooBig.length > 1 ? 's are' : ' is'} larger than 200 MB and ${tooBig.length > 1 ? 'were' : 'was'} skipped`);
  // Without a Files field there is nowhere to keep anything that can't be a cover.
  if (!fileField) {
    const other = ok.filter((f) => !f.type.startsWith('image/'));
    if (other.length) toast.warning(`Add a Files field to ${collection.name} to keep ${other.length > 1 ? 'these files' : 'this file'}`);
    ok = ok.filter((f) => f.type.startsWith('image/'));
  }
  if (!ok.length) return [];
  const toastId = toast({ title: `Adding ${ok.length} file${ok.length > 1 ? 's' : ''}…`, duration: 0 });
  const ids: string[] = [];
  try {
    for (const file of ok) {
      const meta = await ingestFile(file, file.name);
      const kind = fileKind(meta.mime, meta.name);
      const values: Record<string, unknown> = { ...extraValues };
      if (fileField && kind !== 'image') values[fileField.id] = [meta];
      // Auto-tag audio as SFX/Music-ish when the collection has such tags.
      if (tagsField && !values[tagsField.id]) {
        const wanted = kind === 'audio' ? ((meta.duration ?? 0) > 30 ? 'music' : 'sfx') : kind === 'video' ? 'vfx' : null;
        const opt = wanted ? tagsField.config.options?.find((o) => o.label.toLowerCase() === wanted) : undefined;
        if (opt) values[tagsField.id] = [opt.id];
      }
      ids.push(project.createRecord(collection.id, { title: stripExt(file.name), values, cover: kind === 'image' ? meta : null }));
    }
    toast.dismiss(toastId);
    toast.success(`Added ${ids.length} file${ids.length > 1 ? 's' : ''} to ${collection.name}`);
  } catch (e) {
    toast.dismiss(toastId);
    toast.error('Some files could not be added', { description: e instanceof Error ? e.message : String(e) });
  }
  return ids;
}

export function dataTransferHasFiles(dt: DataTransfer | null): boolean {
  return !!dt && Array.from(dt.types).includes('Files');
}
